import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import "./App.css"
import { Routes, Route, useLocation } from "react-router-dom";
import { NavigationBar, Footer } from "./layouts";
import { fetchPageData } from "./utils/apis/fetchPageData";
import { SpinnerLoading } from "./components";
import "aos/dist/aos.css";
import fav_logo from './assets/images/logo-favicon.png';
import Cookies from 'js-cookie';
import { GlobalProvider } from './contexts/GlobalContext';
import { useTranslation } from "./contexts/TranslationContext";
import { DEFAULT_CURRENCY, persistPreferredCurrency } from "./utils/currency";

const Home = lazy(() => import("./pages/Home/Home"));
const CustomPackages = lazy(() => import("./pages/CustomPackages/CustomPackages"));
const PackagesCategoriesList = lazy(() => import("./pages/PackagesCategoriesList/PackagesCategoriesList"));
const PackagesList = lazy(() => import("./pages/PackagesList/PackagesList"));
const PackageDetails = lazy(() => import("./pages/PackageDetails/PackageDetails"));
const Login = lazy(() => import("./pages/Login/Login"));
const ContactUs = lazy(() => import("./pages/ContactUs/ContactUs"));
const CustomPackageDetails = lazy(() => import("./pages/CustomPackageDetails/CustomPackageDetails"));
const Profile = lazy(() => import("./pages/Profile/Profile"));
const AboutUs = lazy(() => import("./pages/AboutUs/AboutUs"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy/PrivacyPolicy"));
const ServicesList = lazy(() => import("./pages/ServicesList/ServicesList"));
const HotelList = lazy(() => import("./pages/HotelList/HotelList"));
const FlightList = lazy(() => import("./pages/FlightList/FlightList"));
const Hotels = lazy(() => import("./pages/Hotels/Hotels"));
const HotelDetailsPage = lazy(() => import("./pages/HotelDetailsPage/HotelDetailsPage"));
const Flights = lazy(() => import("./pages/Flights/Flights"));
const TermsConditions = lazy(() => import("./pages/TermsConditions/TermsConditions"));

const Register = lazy(() => import("./Auth/Register/Register"));
const CheckEmail = lazy(() => import("./Auth/CheckEmail/CheckEmail"));
const ResetPassword = lazy(() => import("./Auth/ResetPassword/ResetPassword"));
const ForgetPassword = lazy(() => import("./Auth/ForgetPassword/ForgetPassword"));
const AddNewPassword = lazy(() => import("./Auth/AddNewPassword/AddNewPassword"));

const HotelInfoForm = lazy(() => import("./Features/HotelList/Components/HotelInfoForm/HotelInfoForm"));
const SuccessFinalStepPage = lazy(() => import("./components/FinalStep/Success/FinalStep"));
const FailedFinalStepPage = lazy(() => import("./components/FinalStep/Failed/FinalStep"));
const NotFoundPage = lazy(() => import("./components/NotFound/NotFound"));
 



function App() {
    const routeFallback = (
      <div className='loading-container'>
        <SpinnerLoading />
      </div>
    );
    const { currentLanguage, setLanguage } = useTranslation();

    const persistAppCookie = (name, value) => {
        Cookies.remove(name);
        Cookies.remove(name, { path: "/" });
        Cookies.set(name, value, { path: "/", expires: 365 });
    };

    const hasStoredPreferences = () =>
      Boolean(
        Cookies.get("country") &&
          Cookies.get("preferred_currency") &&
          Cookies.get("preferred_language")
      );


    useEffect(() => {
        const updateFavicon = () => {
            let link = document.querySelector('link[rel="icon"]');
            if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
            }
            link.href = fav_logo;
        };

        updateFavicon();
    }, []);
    
    useEffect(() => {
        let isCancelled = false;
        let cleanupScheduledTask = () => {};

        const initAOS = async () => {
            const { default: AOS } = await import("aos");
            if (!isCancelled) {
                AOS.init({
                    once: true,
                });
            }
        };

        if ("requestIdleCallback" in window) {
            const idleId = window.requestIdleCallback(() => {
                void initAOS();
            }, { timeout: 1500 });

            cleanupScheduledTask = () => window.cancelIdleCallback(idleId);
        } else {
            const timeoutId = window.setTimeout(() => {
                void initAOS();
            }, 200);

            cleanupScheduledTask = () => window.clearTimeout(timeoutId);
        }

        return () => {
            isCancelled = true;
            cleanupScheduledTask();
        };
    }, []);

    const url = "https://alltimetest7.inter-netsys.com"

    const location = useLocation();
    const pageName = location.pathname.replace(/^\//, "") ? location.pathname.replace(/^\//, "") : "home";
    
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, [location.pathname]);


 



    const [preferencesReady, setPreferencesReady] = useState(hasStoredPreferences);
    const [pageData, setPageData] = useState(null);
    const [loading, setLoading] = useState(!hasStoredPreferences() || pageName === "home" || pageName.toLowerCase() === "packages" || pageName.toLowerCase() === "categories");
    const [isBackgroundRefreshing, setIsBackgroundRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const pageDataRef = useRef(null);
    const previousLanguageRef = useRef(currentLanguage);
    const previousPageRequestRef = useRef(`${pageName}?${location.search}`);

    useEffect(() => {
        pageDataRef.current = pageData;
    }, [pageData]);


    useEffect(() => {
        if (!preferencesReady) {
            return;
        }

        const getPageData = async () => {
            const currentPageRequest = `${pageName}?${location.search}`;
            const isLanguageRefresh =
              previousLanguageRef.current !== currentLanguage &&
              previousPageRequestRef.current === currentPageRequest &&
              pageDataRef.current !== null;

            if (isLanguageRefresh) {
              setIsBackgroundRefreshing(true);
            } else {
              setLoading(true);
            }

            const { data, error } = await fetchPageData(pageName);
            setPageData(data);
            setError(error);
            setLoading(false);
            setIsBackgroundRefreshing(false);
            previousLanguageRef.current = currentLanguage;
            previousPageRequestRef.current = currentPageRequest;
        };

        const query = new URLSearchParams(location.search);
        const hasFilters =
          !!query.get("search") ||
          !!query.get("category") ||
          !!query.get("destinations");

        if (
          pageName === "home" ||
          pageName.toLowerCase() === "about" ||
          pageName.toLowerCase() === "contact" ||
          pageName.toLowerCase() === "categories" ||
          pageName.toLowerCase() === "services" ||
          pageName.toLowerCase() === "privacy" ||
          pageName.toLowerCase() === "terms" ||
          (pageName.toLowerCase() === "packages" && !hasFilters)
        ) {
          getPageData();
          return;
        }

        setLoading(false);
        setIsBackgroundRefreshing(false);
        previousLanguageRef.current = currentLanguage;
        previousPageRequestRef.current = `${pageName}?${location.search}`;

    }, [pageName, location.search, currentLanguage, preferencesReady]);


async function getUserCountry() {
  console.log("Fetching GeoIP...");
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 2500);

  try {
    const res = await fetch("https://ipapi.co/json/", {
      signal: controller.signal,
    });
    if (!res.ok) throw new Error("GeoIP request failed");
    const data = await res.json();

    console.log("GeoIP Response:", data);
    return data;
  } catch (err) {
    console.error("Error fetching GeoIP:", err);
    // Return default data if fetch fails
    return { country_code: "US", currency: DEFAULT_CURRENCY, languages: "en" };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

useEffect(() => {
  let isCancelled = false;

  const initPreferences = async () => {
    const existingCountry = Cookies.get("country");
    const existingCurrency = Cookies.get("preferred_currency");
    const existingLanguage = Cookies.get("preferred_language");

    if (existingCountry && existingCurrency && existingLanguage) {
      console.log("Cookies already exist:", {
        country: existingCountry,
        preferred_currency: existingCurrency,
        language: existingLanguage,
      });
      setPreferencesReady(true);
      return;
    }

    const needsGeoIP = !existingCountry || !existingCurrency || !existingLanguage;
    const data = needsGeoIP ? await getUserCountry() : {};

    if (isCancelled) {
      return;
    }
    const fallbackCurrency =
      existingCurrency || data.currency || data.preferred_currency || DEFAULT_CURRENCY;
    const fallbackCountry = existingCountry || data.country_code || "US";
    const preferredLang =
      existingLanguage ||
      (typeof data.languages === "string" && data.languages.includes("ar")
        ? "ar"
        : "en");

    if (!existingCountry && fallbackCountry) {
      persistAppCookie("country", fallbackCountry);
    }

    if (!existingCurrency && fallbackCurrency) {
      persistPreferredCurrency(fallbackCurrency);
    }

    if (!existingLanguage && preferredLang) {
      setLanguage(preferredLang);
    }

    setPreferencesReady(true);

    console.log("Cookies initialized:", {
      country: fallbackCountry,
      preferred_currency: fallbackCurrency,
      language: preferredLang,
    });
  };

  initPreferences();

  return () => {
    isCancelled = true;
  };
}, [setLanguage]);






    



    if (!preferencesReady || loading) return <div className='loading-container'>
        <SpinnerLoading />
    </div>;


    
    return (
      // className="App"
      <GlobalProvider>
              <div className={`App`}>
        {isBackgroundRefreshing && (
          <div className="app-loading-overlay" aria-live="polite" aria-busy="true">
            <SpinnerLoading />
          </div>
        )}
        <NavigationBar url={url} />
        <main className="app-content">
          <Suspense fallback={routeFallback}>
            <Routes>
              <Route
                index
                element={<Home pageData={pageData?.content} url={url} />}
              />

              <Route
                path="/home"
                element={<Home pageData={pageData?.content} url={url} />}
              />

              <Route
                path="/hotels"
                element={<HotelList pageData={pageData} url={url} />}
              />

              <Route
                path="/flights"
                element={<FlightList pageData={pageData} url={url} />}
              />

              <Route
                path="/about"
                element={<AboutUs pageData={pageData} url={url} />}
              />

              <Route
                path="/privacy"
                element={<PrivacyPolicy pageData={pageData} url={url} />}
              />

              <Route
                path="/terms"
                element={<TermsConditions pageData={pageData} url={url} />}
              />

              <Route
                path="/categories"
                element={
                  <PackagesCategoriesList
                    pageData={pageData}
                    url={url}
                    errorNotFound={error}
                  />
                }
              />

              <Route
                path="/services"
                element={
                  <ServicesList
                    pageData={pageData}
                    url={url}
                    errorNotFound={error}
                  />
                }
              />

              <Route
                path="/Packages"
                element={<PackagesList pageData={pageData} url={url} />}
              />

              <Route path="/hotel" element={<Hotels url={url} />} />
              <Route path="/hotelinfo" element={<HotelInfoForm url={url} />} />
              <Route path="/flight" element={<Flights url={url} />} />

              <Route path="/myPackages" element={<CustomPackages url={url} />} />
              <Route
                path="/packages/:category"
                element={
                  <PackagesList
                    pageData={pageData}
                    url={url}
                    errorNotFound={error}
                  />
                }
              />

              <Route path="/package/:id" element={<PackageDetails url={url} />} />

              <Route path="/hotels/:id" element={<HotelDetailsPage url={url} />} />
              <Route path="/login" element={<Login url={url} />} />
              <Route path="/register" element={<Register url={url} />} />
              <Route path="/checkemail" element={<CheckEmail url={url} />} />
              <Route
                path="/resetpassword/:userIdentifier/:code"
                element={<ResetPassword url={url} />}
              />
              <Route
                path="/forgetpassword"
                element={<ForgetPassword url={url} />}
              />

              <Route
                path="/contact"
                element={<ContactUs url={url} pageData={pageData} />}
              />
              <Route
                path="/success-final-step"
                element={<SuccessFinalStepPage url={url} pageData={pageData} />}
              />
              <Route
                path="/failed-final-step"
                element={<FailedFinalStepPage url={url} pageData={pageData} />}
              />
              <Route path="/profile" element={<Profile url={url} />} />
              <Route
                path="/myPackages/:id/*"
                element={<CustomPackageDetails url={url} />}
              />
              <Route
                path="/newpassword/:userIdentifier"
                element={<AddNewPassword url={url} />}
              />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
      </GlobalProvider>

    );
}

export default App;
